import { useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "../auth/AuthContext";
import { Button } from "@/components/ui/button";
import { GoogleIcon } from "@/components/icons/GoogleIcon";
import { useTheme } from "@/components/ThemeProvider";
import { useBrand } from "@/branding/BrandProvider";
import { Eyebrow } from "@/components/PageUi";
import { DevLoginPanel } from "../auth/DevLoginPanel";
import { EmailCodeSignIn } from "@dolfin/ui";

export function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const { theme } = useTheme();
  const brand = useBrand();
  const logo = theme === "dark" ? brand.logoDark : brand.logoLight;
  const [error, setError] = useState<string | null>(null);

  const googleLogin = useGoogleLogin({
    flow: "implicit",
    onSuccess: async (tokenResponse) => {
      setError(null);
      const result = await login(tokenResponse.access_token);
      if (result.error) {
        setError(result.error);
      }
    },
    onError: () => setError("Google sign-in failed. Please try again."),
  });

  if (isAuthenticated) return <Navigate to="/" replace />;

  return (
    <AuthPanel logo={logo} brandName={brand.name} title="Welcome back" description="Sign in to your account" error={error}>
      <Button variant="outline" size="lg" className="w-full" onClick={() => googleLogin()}>
        <GoogleIcon />
        Continue with Google
      </Button>
      <EmailCodeSignIn onProof={async (proof) => (await login(proof, "email")).error ?? null} />
      <DevLoginPanel onError={setError} />
    </AuthPanel>
  );
}

export function AuthPanel({
  logo,
  brandName,
  title,
  description,
  error,
  children,
}: {
  logo: string;
  brandName: string;
  title: ReactNode;
  description?: ReactNode;
  error?: string | null;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-h-[calc(100svh-3.5rem)] items-center justify-center p-4">
      <section className="w-full max-w-sm rounded-xl border bg-card/80 p-8 text-center shadow-xl shadow-foreground/5 backdrop-blur-xl">
        <img src={logo} alt={brandName} className="mx-auto size-14" />
        <Eyebrow className="mt-6">{brandName}</Eyebrow>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">{title}</h1>
        {description ? <p className="mt-1 text-sm text-pretty text-muted-foreground">{description}</p> : null}
        {children ? <div className="mt-6 flex flex-col items-center gap-3">{children}</div> : null}
        {error ? (
          <p className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </section>
    </div>
  );
}
